import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { HttpService } from "src/app/services/http.service";
import { RecipientsGroup } from "./recipients-group";
import { InvestorSuggestion } from "./investor-suggestion";
import { RecipientType } from "./recipient-type";
import MetaFileLink from "src/app/models/metaFileLink.model";
import { Subject, Observable, timer, of } from "rxjs";
import { catchError, exhaustMap, filter, take, tap } from "rxjs/operators";

export enum InvestorCommunicationJobStatus {
  Queued = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3
}

export interface InvestorCommunicationJobResponse {
  jobId: number;
  status: InvestorCommunicationJobStatus;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  createTimestamp: string;
  startedAtUtc?: string;
  finishedAtUtc?: string;
  errorMessage?: string;
  progressPercent: number;
}

@Injectable({
  providedIn: "root",
})
export class InvestorCommunicationService {
  public readonly INVESTORCOMMUNICATION_BASE_ENDPOINT = "investorcommunication";
  private readonly MAX_NUM_OF_RECIPIENTS = 2000;

  constructor(private http: HttpService) {}

  getGroups(filterQuery?: string) {
    return this.http.getTyped<RecipientsGroup[]>(
      `${this.INVESTORCOMMUNICATION_BASE_ENDPOINT}/getgroups${
        filterQuery ? `?filter=${filterQuery}` : ""
      }`
    );
  }

  /**
   * Send investor emails synchronously (legacy, may timeout for large lists)
   * @deprecated Use sendInvestorsMailsAsync instead
   */
  sendInvestorsMails(templateId: number, model: any) {
    return this.http.post(
      `${this.INVESTORCOMMUNICATION_BASE_ENDPOINT}/${templateId}`,
      model
    );
  }

  /**
   * Send investor emails asynchronously via background job.
   * Returns job ID immediately, use pollJobStatus to track progress.
   */
  sendInvestorsMailsAsync(templateId: number, model: any): Observable<InvestorCommunicationJobResponse> {
    return this.http.postTyped<InvestorCommunicationJobResponse>(
      `${this.INVESTORCOMMUNICATION_BASE_ENDPOINT}/sendAsync/${templateId}`,
      model
    );
  }

  /**
   * Get current status of an email job
   */
  getJobStatus(jobId: number): Observable<InvestorCommunicationJobResponse> {
    return this.http.getTyped<InvestorCommunicationJobResponse>(
      `${this.INVESTORCOMMUNICATION_BASE_ENDPOINT}/job/${jobId}`
    );
  }

  /**
   * Poll job status until the job row exists (first successful GET).
   * Emits exactly once and completes.
   * @param jobId Job ID to poll
   * @returns Observable that emits job status once the job exists
   */
  pollJobStatus(jobId: number): Observable<InvestorCommunicationJobResponse> {
    return timer(0, 2000).pipe(
      exhaustMap(() =>
        this.getJobStatus(jobId).pipe(
          catchError((err: HttpErrorResponse) => {
            // Job row might not exist yet right after creation request; keep polling on 404.
            if (err?.status === 404) return of(null);
            throw err;
          })
        )
      ),
      filter((status): status is InvestorCommunicationJobResponse => status !== null),
      tap(status => console.log(`Job ${jobId} status: ${InvestorCommunicationJobStatus[status.status]} (${status.sentCount}/${status.totalRecipients})`)),
      take(1)
    );
  }

  generateSubmitModel(formValues: {
    recipients: InvestorSuggestion[];
    bccRecipients: InvestorSuggestion[];
    subject: string;
    message: string;
    fileattachments: MetaFileLink[]
  }) {
    const recipientsByType = [
      ...formValues.recipients,
      ...formValues.bccRecipients,
    ].reduce(
      (acc, val) => ({
        ...acc,
        [val.recipientType]: acc[val.recipientType]
          ? [
              ...acc[val.recipientType],
              val.recipientType !== RecipientType.Group ? val.id : val,
            ]
          : [val.recipientType !== RecipientType.Group ? val.id : val],
      }),
      {}
    );
    var metaFileLinkIds = formValues.fileattachments.filter((file) => file && file.id).map((file) => file.id);
    return {
      subject: formValues.subject,
      message: formValues.message || null,
      recipients: recipientsByType[RecipientType.Contact],
      users: recipientsByType[RecipientType.User],
      groups: recipientsByType[RecipientType.Group],
      metaFileLinkIds
    };
  }
}
